// src/index.ts
function parseHAR(input) {
  try {
    const data = JSON.parse(input);
    if (!data.log) {
      return {
        success: false,
        error: "Invalid HAR format",
        details: 'Missing "log" property'
      };
    }
    if (!data.log.entries || !Array.isArray(data.log.entries)) {
      return {
        success: false,
        error: "Invalid HAR format",
        details: 'Missing or invalid "entries" array'
      };
    }
    const har = data;
    const baseTime = new Date(har.log.entries[0]?.startedDateTime || Date.now()).getTime();
    har.log.entries = har.log.entries.map((entry, index) => {
      const startTime = new Date(entry.startedDateTime).getTime() - baseTime;
      return {
        ...entry,
        _id: `entry-${index}`,
        _startTime: startTime,
        _endTime: startTime + (entry.time || 0)
      };
    });
    const stats = computeStats(har);
    return {
      success: true,
      data: har,
      stats
    };
  } catch (e) {
    return {
      success: false,
      error: "Failed to parse HAR file",
      details: e instanceof Error ? e.message : "Unknown error"
    };
  }
}
function computeStats(har) {
  const entries = har.log.entries;
  const byStatus = {};
  const byMethod = {};
  const byContentType = {};
  const byDomain = {};
  let totalSize = 0;
  let errors = 0;
  let redirects = 0;
  for (const entry of entries) {
    const statusGroup = `${Math.floor(entry.response.status / 100)}xx`;
    byStatus[statusGroup] = (byStatus[statusGroup] || 0) + 1;
    if (entry.response.status >= 400) errors++;
    if (entry.response.status >= 300 && entry.response.status < 400) redirects++;
    const method = entry.request.method.toUpperCase();
    byMethod[method] = (byMethod[method] || 0) + 1;
    const contentType = simplifyContentType(entry.response.content.mimeType);
    byContentType[contentType] = (byContentType[contentType] || 0) + 1;
    const domain = extractDomain(entry.request.url);
    byDomain[domain] = (byDomain[domain] || 0) + 1;
    totalSize += entry.response.content.size || 0;
  }
  const times = entries.map((e) => new Date(e.startedDateTime).getTime());
  const endTimes = entries.map((e) => new Date(e.startedDateTime).getTime() + (e.time || 0));
  return {
    totalRequests: entries.length,
    totalSize,
    totalTime: Math.max(...endTimes) - Math.min(...times),
    startTime: new Date(Math.min(...times)),
    endTime: new Date(Math.max(...endTimes)),
    byStatus,
    byMethod,
    byContentType,
    byDomain,
    errors,
    redirects
  };
}
function getTimingBreakdown(timings) {
  const phases = [];
  let offset = 0;
  const addPhase = (name, value, color) => {
    const duration = value && value > 0 ? value : 0;
    if (duration > 0) {
      phases.push({ name, duration, offset, color });
      offset += duration;
    }
  };
  addPhase("Blocked", timings.blocked, "#808080");
  addPhase("DNS", timings.dns, "#8BC34A");
  addPhase("Connect", timings.connect, "#FF9800");
  addPhase("SSL", timings.ssl, "#9C27B0");
  addPhase("Send", timings.send, "#2196F3");
  addPhase("Wait", timings.wait, "#4CAF50");
  addPhase("Receive", timings.receive, "#03A9F4");
  return phases;
}
function filterEntries(entries, filters) {
  return entries.filter((entry) => {
    if (filters.search) {
      if (filters.isRegex) {
        try {
          const regex = new RegExp(filters.search, "i");
          if (!regex.test(entry.request.url)) return false;
        } catch {
          if (!entry.request.url.toLowerCase().includes(filters.search.toLowerCase())) return false;
        }
      } else {
        const searchLower = filters.search.toLowerCase();
        const urlMatch = entry.request.url.toLowerCase().includes(searchLower);
        if (!urlMatch) return false;
      }
    }
    if (filters.methods.length > 0) {
      if (!filters.methods.includes(entry.request.method.toUpperCase())) {
        return false;
      }
    }
    if (filters.statusCodes.length > 0) {
      const status = entry.response.status;
      const statusGroup = `${Math.floor(status / 100)}xx`;
      const statusStr = status.toString();
      const matchesGroup = filters.statusCodes.includes(statusGroup);
      const matchesSpecific = filters.statusCodes.includes(statusStr);
      if (!matchesGroup && !matchesSpecific) {
        return false;
      }
    }
    if (filters.contentTypes.length > 0) {
      const contentType = simplifyContentType(entry.response.content.mimeType);
      if (!filters.contentTypes.includes(contentType)) {
        return false;
      }
    }
    if (filters.domains.length > 0) {
      const domain = extractDomain(entry.request.url);
      if (!filters.domains.includes(domain)) {
        return false;
      }
    }
    const size = entry.response.content.size || 0;
    if (filters.minSize !== void 0 && size < filters.minSize) return false;
    if (filters.maxSize !== void 0 && size > filters.maxSize) return false;
    const time = entry.time || 0;
    if (filters.minTime !== void 0 && time < filters.minTime) return false;
    if (filters.maxTime !== void 0 && time > filters.maxTime) return false;
    return true;
  });
}
function formatBytes(bytes) {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}
function formatTime(ms) {
  if (ms < 1) return "<1ms";
  if (ms < 1e3) return `${Math.round(ms)}ms`;
  if (ms < 6e4) return `${(ms / 1e3).toFixed(2)}s`;
  return `${(ms / 6e4).toFixed(2)}m`;
}
function extractDomain(url) {
  try {
    return new URL(url).hostname;
  } catch {
    return "unknown";
  }
}
function getFileName(url) {
  try {
    const pathname = new URL(url).pathname;
    const segments = pathname.split("/").filter(Boolean);
    return segments[segments.length - 1] || pathname || "/";
  } catch {
    return url;
  }
}
function simplifyContentType(mimeType) {
  if (!mimeType) return "other";
  const type = mimeType.split(";")[0].trim().toLowerCase();
  if (type.includes("javascript") || type.includes("ecmascript")) return "js";
  if (type.includes("css")) return "css";
  if (type.includes("html")) return "html";
  if (type.includes("json")) return "json";
  if (type.includes("xml")) return "xml";
  if (type.includes("image")) return "image";
  if (type.includes("font")) return "font";
  if (type.includes("video")) return "video";
  if (type.includes("audio")) return "audio";
  if (type.includes("text")) return "text";
  return "other";
}
function getStatusColor(status) {
  if (status < 200) return "text-gray-500";
  if (status < 300) return "text-green-500";
  if (status < 400) return "text-blue-500";
  if (status < 500) return "text-yellow-500";
  return "text-red-500";
}
function redactHAR(har, options = {}) {
  const sensitiveHeaders = [
    "authorization",
    "cookie",
    "set-cookie",
    "x-api-key",
    "x-auth-token",
    "x-csrf-token",
    ...options.additionalHeaders || []
  ];
  const redactValue = options.redactValue || "[REDACTED]";
  const redactHeaders = (headers) => {
    return headers.map((h) => {
      if (sensitiveHeaders.includes(h.name.toLowerCase())) {
        return { ...h, value: redactValue };
      }
      return h;
    });
  };
  const redactCookies = (cookies) => {
    return cookies.map((c) => ({ ...c, value: redactValue }));
  };
  return {
    log: {
      ...har.log,
      entries: har.log.entries.map((entry) => ({
        ...entry,
        request: {
          ...entry.request,
          headers: redactHeaders(entry.request.headers),
          cookies: redactCookies(entry.request.cookies),
          postData: entry.request.postData && options.redactPostData ? { ...entry.request.postData, text: redactValue } : entry.request.postData
        },
        response: {
          ...entry.response,
          headers: redactHeaders(entry.response.headers),
          cookies: redactCookies(entry.response.cookies),
          content: options.redactResponseContent ? { ...entry.response.content, text: redactValue } : entry.response.content
        }
      }))
    }
  };
}
function generateCurl(entry) {
  const { request } = entry;
  const parts = ["curl"];
  if (request.method !== "GET") {
    parts.push(`-X ${request.method}`);
  }
  parts.push(`'${request.url}'`);
  const skipHeaders = [
    ":authority",
    ":method",
    ":path",
    ":scheme",
    "content-length",
    "host",
    "connection"
  ];
  for (const header of request.headers) {
    if (!skipHeaders.includes(header.name.toLowerCase())) {
      const value = header.value.replace(/'/g, "'\\''");
      parts.push(`-H '${header.name}: ${value}'`);
    }
  }
  if (request.postData?.text) {
    const body = request.postData.text.replace(/'/g, "'\\''");
    parts.push(`--data-raw '${body}'`);
  }
  return parts.join(" \\\n  ");
}
function exportAsHAR(har, entries) {
  const exported = {
    log: {
      ...har.log,
      entries
    }
  };
  return JSON.stringify(exported, null, 2);
}
function exportAsCSV(entries) {
  const headers = ["#", "Method", "URL", "Status", "Type", "Size", "Time"];
  const rows = entries.map((entry, i) => [
    i + 1,
    entry.request.method,
    `"${entry.request.url.replace(/"/g, '""')}"`,
    entry.response.status,
    entry.response.content.mimeType || "",
    entry.response.content.size || 0,
    entry.time || 0
  ]);
  return [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");
}
function getInitiatorChain(entry, allEntries) {
  const chain = [];
  const initiator = entry._initiator;
  if (!initiator) {
    return chain;
  }
  chain.push({
    type: initiator.type,
    url: initiator.url,
    lineNumber: initiator.lineNumber,
    columnNumber: initiator.columnNumber
  });
  if (initiator.stack) {
    let stack = initiator.stack;
    while (stack) {
      for (const frame of stack.callFrames) {
        if (frame.url) {
          chain.push({
            type: "script",
            url: frame.url,
            lineNumber: frame.lineNumber,
            columnNumber: frame.columnNumber,
            functionName: frame.functionName
          });
        }
      }
      stack = stack.parent;
    }
  }
  if (initiator.url) {
    const initiatingEntry = allEntries.find((e) => e.request.url === initiator.url);
    if (initiatingEntry && initiatingEntry._initiator) {
      const parentChain = getInitiatorChain(initiatingEntry, allEntries);
      if (parentChain.length > 0 && chain.length < 10) {
        chain.push(...parentChain);
      }
    }
  }
  return chain;
}
var index_default = {
  parseHAR,
  computeStats,
  getTimingBreakdown,
  filterEntries,
  formatBytes,
  formatTime,
  extractDomain,
  getFileName,
  simplifyContentType,
  getStatusColor,
  redactHAR,
  generateCurl,
  exportAsHAR,
  exportAsCSV,
  getInitiatorChain
};
export {
  computeStats,
  index_default as default,
  exportAsCSV,
  exportAsHAR,
  extractDomain,
  filterEntries,
  formatBytes,
  formatTime,
  generateCurl,
  getFileName,
  getInitiatorChain,
  getStatusColor,
  getTimingBreakdown,
  parseHAR,
  redactHAR,
  simplifyContentType
};
